import React, { useEffect, useRef, useState } from 'react';

const FONT = "'DM Sans', sans-serif";
const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

const parse = (hhmm) => {
  if (!hhmm || !/^\d{1,2}:\d{2}/.test(hhmm)) return null;
  const [h, m] = hhmm.split(':').map(Number);
  return { h12: h % 12 === 0 ? 12 : h % 12, m, ap: h < 12 ? 'AM' : 'PM' };
};
const build = (h12, m, ap) => {
  let h = h12 % 12;
  if (ap === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
const pad = (n) => String(n).padStart(2, '0');
export const formatTime12 = (hhmm) => {
  const t = parse(hhmm);
  return t ? `${t.h12}:${pad(t.m)} ${t.ap}` : '';
};

export default function TimePicker({
  value,
  onChange,
  placeholder = 'Select time',
  align = 'left',          // 'left' | 'right' — which edge the popover lines up with
  accent = '#101F40',
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [h12, setH12] = useState(9);
  const [min, setMin] = useState(0);
  const [ap, setAp] = useState('AM');
  const wrapRef = useRef(null);

  const openPicker = () => {
    const t = parse(value) || { h12: 9, m: 0, ap: 'AM' };
    setH12(t.h12); setMin(t.m); setAp(t.ap);
    // Open upwards when there isn't room below (e.g. bottom-sheet modal on mobile)
    const rect = wrapRef.current?.getBoundingClientRect();
    setOpenUp(rect ? window.innerHeight - rect.bottom < 360 && rect.top > 360 : false);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const apply = () => { onChange(build(h12, min, ap)); setOpen(false); };
  const clear = () => { onChange(''); setOpen(false); };

  const cell = (on) => ({
    border: 'none', borderRadius: 8, padding: '8px 0', fontSize: 13.5, fontFamily: FONT,
    cursor: 'pointer', fontWeight: on ? 700 : 500,
    background: on ? accent : 'transparent',
    color: on ? '#fff' : '#374151',
    transition: 'background 0.12s, color 0.12s',
  });
  const sectionLabel = { fontSize: 11.5, fontWeight: 600, color: '#9ca3af', margin: '12px 0 6px' };

  // A minute that isn't on the 5-minute grid (e.g. 9:39 from an existing punch) still shows as selected
  const minuteOptions = MINUTES.includes(min) ? MINUTES : [...MINUTES, min].sort((a, b) => a - b);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPicker())}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          width: '100%', padding: '9px 12px', minHeight: 40, boxSizing: 'border-box',
          border: `1px solid ${open || value ? accent : '#e8e4df'}`, borderRadius: 8,
          background: '#fff', fontSize: 14, fontFamily: FONT, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, textAlign: 'left',
        }}
      >
        <span style={{ color: value ? '#101F40' : '#9ca3af', fontWeight: value ? 600 : 400 }}>
          {value ? formatTime12(value) : placeholder}
        </span>
        <svg aria-hidden="true" viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#9ca3af" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" />
        </svg>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Choose a time"
          style={{
            position: 'absolute', zIndex: 10001, width: 264,
            [align === 'right' ? 'right' : 'left']: 0,
            ...(openUp ? { bottom: 'calc(100% + 6px)' } : { top: 'calc(100% + 6px)' }),
            background: '#fff', border: '1px solid #e8e4df', borderRadius: 14,
            boxShadow: '0 16px 40px rgba(16,31,64,0.2)', padding: 14, fontFamily: FONT,
          }}
        >
          {/* Live preview */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#101F40', letterSpacing: '-0.5px' }}>
              {h12}:{pad(min)} <span style={{ fontSize: 15, fontWeight: 600, color: '#6b7280' }}>{ap}</span>
            </div>
            {/* AM / PM toggle */}
            <div role="radiogroup" aria-label="AM or PM" style={{ display: 'flex', background: '#f5f3ef', borderRadius: 9, padding: 3, gap: 2 }}>
              {['AM', 'PM'].map(p => (
                <button key={p} type="button" role="radio" aria-checked={ap === p} onClick={() => setAp(p)}
                  style={{
                    border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12.5, fontWeight: 700,
                    fontFamily: FONT, cursor: 'pointer',
                    background: ap === p ? '#fff' : 'transparent',
                    color: ap === p ? '#101F40' : '#9ca3af',
                    boxShadow: ap === p ? '0 1px 3px rgba(16,31,64,0.15)' : 'none',
                  }}>{p}</button>
              ))}
            </div>
          </div>

          <div style={sectionLabel}>Hour</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4 }}>
            {HOURS.map(h => (
              <button key={h} type="button" onClick={() => setH12(h)} style={cell(h12 === h)}>{h}</button>
            ))}
          </div>

          <div style={sectionLabel}>Minute</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4 }}>
            {minuteOptions.map(m => (
              <button key={m} type="button" onClick={() => setMin(m)} style={cell(min === m)}>{pad(m)}</button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="button" onClick={clear}
              style={{ padding: '9px 14px', border: '1px solid #e8e4df', borderRadius: 9, background: '#fff', color: '#6b7280', fontSize: 13, fontWeight: 500, fontFamily: FONT, cursor: 'pointer' }}>
              Clear
            </button>
            <button type="button" onClick={apply}
              style={{ flex: 1, padding: '9px 14px', border: 'none', borderRadius: 9, background: accent, color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: FONT, cursor: 'pointer' }}>
              Set {h12}:{pad(min)} {ap}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
